/**
 * Illustrationsbibliothek fuer den Creator Studio
 * 50+ unDraw-style SVG Illustrationen, organisiert nach Kategorien
 *
 * Alle Illustrationen sind lizenzfrei und koennen farblich angepasst werden.
 * Die primaryColor (#6366F1) kann durch jede beliebige Farbe ersetzt werden.
 */

import type { IllustrationElement } from './elementTypes';

// ===== HELPER FUNCTION =====

/**
 * Ersetzt die Primaerfarbe einer Illustration durch eine neue Farbe
 * @param svg - Der SVG-String der Illustration
 * @param newColor - Die neue Farbe (Hex, RGB, etc.)
 * @returns Der modifizierte SVG-String
 */
export function recolorIllustration(svg: string, newColor: string): string {
  // Ersetze die Standard-Primaerfarbe (#6366F1) und gaengige Varianten
  return svg
    .replace(/#6366F1/gi, newColor)
    .replace(/#6366f1/gi, newColor)
    .replace(/fill="#6366F1"/gi, `fill="${newColor}"`)
    .replace(/stroke="#6366F1"/gi, `stroke="${newColor}"`);
}

// ===== BUSINESS ILLUSTRATIONEN =====
const businessIllustrations: IllustrationElement[] = [
  {
    id: 'illus-meeting',
    type: 'illustration',
    name: 'Team Meeting',
    tags: ['meeting', 'team', 'zusammenarbeit', 'buero', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="60" width="140" height="70" rx="4" fill="#E5E7EB"/>
      <rect x="35" y="65" width="130" height="60" rx="2" fill="#F3F4F6"/>
      <circle cx="60" cy="40" r="15" fill="#6366F1"/>
      <path d="M45 95 L45 80 Q60 70 75 80 L75 95" fill="#6366F1"/>
      <circle cx="100" cy="35" r="18" fill="#A5B4FC"/>
      <path d="M80 100 L80 82 Q100 70 120 82 L120 100" fill="#A5B4FC"/>
      <circle cx="145" cy="40" r="15" fill="#6366F1"/>
      <path d="M130 95 L130 80 Q145 70 160 80 L160 95" fill="#6366F1"/>
      <rect x="85" y="90" width="30" height="20" rx="2" fill="#D1D5DB"/>
      <line x1="90" y1="95" x2="110" y2="95" stroke="#9CA3AF" stroke-width="2"/>
      <line x1="90" y1="100" x2="105" y2="100" stroke="#9CA3AF" stroke-width="2"/>
    </svg>`
  },
  {
    id: 'illus-presentation',
    type: 'illustration',
    name: 'Praesentation',
    tags: ['praesentation', 'vortrag', 'chart', 'business', 'meeting'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="50" y="20" width="100" height="80" rx="4" fill="#E5E7EB"/>
      <rect x="55" y="25" width="90" height="70" rx="2" fill="white"/>
      <rect x="65" y="70" width="15" height="20" fill="#6366F1"/>
      <rect x="85" y="55" width="15" height="35" fill="#A5B4FC"/>
      <rect x="105" y="45" width="15" height="45" fill="#6366F1"/>
      <rect x="125" y="60" width="15" height="30" fill="#A5B4FC"/>
      <rect x="95" y="100" width="10" height="30" fill="#9CA3AF"/>
      <rect x="80" y="125" width="40" height="8" rx="2" fill="#D1D5DB"/>
      <circle cx="30" cy="90" r="12" fill="#6366F1"/>
      <path d="M18 130 L18 110 Q30 100 42 110 L42 130" fill="#6366F1"/>
      <line x1="25" y1="85" x2="50" y2="60" stroke="#6366F1" stroke-width="2" stroke-dasharray="4"/>
    </svg>`
  },
  {
    id: 'illus-analytics',
    type: 'illustration',
    name: 'Analytics Dashboard',
    tags: ['analytics', 'dashboard', 'daten', 'statistik', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="20" width="160" height="110" rx="8" fill="#E5E7EB"/>
      <rect x="25" y="25" width="150" height="100" rx="4" fill="white"/>
      <rect x="35" y="35" width="60" height="40" rx="4" fill="#F3F4F6"/>
      <path d="M45 65 L55 55 L65 60 L80 45" stroke="#6366F1" stroke-width="3" stroke-linecap="round"/>
      <circle cx="80" cy="45" r="4" fill="#6366F1"/>
      <rect x="105" y="35" width="60" height="40" rx="4" fill="#F3F4F6"/>
      <circle cx="135" cy="55" r="15" fill="none" stroke="#6366F1" stroke-width="8"/>
      <path d="M135 40 A15 15 0 0 1 150 55" stroke="#A5B4FC" stroke-width="8"/>
      <rect x="35" y="85" width="130" height="30" rx="4" fill="#F3F4F6"/>
      <rect x="45" y="95" width="40" height="10" rx="2" fill="#6366F1"/>
      <rect x="95" y="95" width="25" height="10" rx="2" fill="#A5B4FC"/>
      <rect x="130" y="95" width="25" height="10" rx="2" fill="#C7D2FE"/>
    </svg>`
  },
  {
    id: 'illus-growth',
    type: 'illustration',
    name: 'Wachstum',
    tags: ['wachstum', 'growth', 'erfolg', 'aufstieg', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="120" width="25" height="20" rx="2" fill="#E5E7EB"/>
      <rect x="65" y="100" width="25" height="40" rx="2" fill="#C7D2FE"/>
      <rect x="100" y="75" width="25" height="65" rx="2" fill="#A5B4FC"/>
      <rect x="135" y="45" width="25" height="95" rx="2" fill="#6366F1"/>
      <path d="M25 115 L55 95 L90 70 L125 40 L170 15" stroke="#6366F1" stroke-width="3" stroke-linecap="round" stroke-dasharray="6 4"/>
      <circle cx="170" cy="15" r="8" fill="#6366F1"/>
      <polygon points="175,10 180,20 170,20" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-teamwork',
    type: 'illustration',
    name: 'Teamwork',
    tags: ['team', 'zusammenarbeit', 'puzzle', 'kooperation', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M60 50 L90 50 L90 60 Q80 65 90 70 L90 80 L60 80 L60 70 Q70 65 60 60 Z" fill="#6366F1"/>
      <path d="M90 50 L90 60 Q100 65 90 70 L90 80 L120 80 L120 70 Q110 65 120 60 L120 50 L110 50 Q105 40 100 50 Z" fill="#A5B4FC"/>
      <path d="M60 80 L90 80 L90 90 Q80 95 90 100 L90 110 L60 110 L60 100 Q70 95 60 90 Z" fill="#C7D2FE"/>
      <path d="M90 80 L120 80 L120 90 Q110 95 120 100 L120 110 L90 110 L90 100 Q100 95 90 90 Z" fill="#6366F1"/>
      <circle cx="45" cy="65" r="12" fill="#6366F1"/>
      <path d="M33 95 L33 80 Q45 73 57 80 L57 95" fill="#6366F1"/>
      <circle cx="155" cy="65" r="12" fill="#A5B4FC"/>
      <path d="M143 95 L143 80 Q155 73 167 80 L167 95" fill="#A5B4FC"/>
    </svg>`
  },
  {
    id: 'illus-contract',
    type: 'illustration',
    name: 'Vertrag',
    tags: ['vertrag', 'contract', 'dokument', 'unterschrift', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="50" y="15" width="100" height="120" rx="4" fill="white" stroke="#E5E7EB" stroke-width="2"/>
      <line x1="65" y1="35" x2="135" y2="35" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="65" y1="50" x2="135" y2="50" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="65" y1="65" x2="135" y2="65" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="65" y1="80" x2="110" y2="80" stroke="#E5E7EB" stroke-width="3"/>
      <rect x="65" y="95" width="70" height="25" rx="2" fill="#F3F4F6"/>
      <path d="M75 107 Q85 100 95 107 Q105 114 115 107" stroke="#6366F1" stroke-width="2" fill="none"/>
      <circle cx="145" cy="115" r="20" fill="#6366F1"/>
      <polyline points="137,115 143,121 155,109" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>
    </svg>`
  },
  {
    id: 'illus-strategy',
    type: 'illustration',
    name: 'Strategie',
    tags: ['strategie', 'planung', 'schach', 'taktik', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="40" y="100" width="120" height="10" rx="2" fill="#E5E7EB"/>
      <rect x="40" y="90" width="120" height="10" fill="#F3F4F6"/>
      <rect x="40" y="90" width="15" height="10" fill="#6366F1"/>
      <rect x="70" y="90" width="15" height="10" fill="white"/>
      <rect x="100" y="90" width="15" height="10" fill="#6366F1"/>
      <rect x="130" y="90" width="15" height="10" fill="white"/>
      <path d="M80 85 L80 60 L70 60 L70 55 L90 55 L90 60 L80 60 L80 70 Q85 72 85 77 Q85 85 80 85" fill="#6366F1"/>
      <path d="M120 85 L120 45 L110 45 L110 35 L115 35 L115 30 L125 30 L125 35 L130 35 L130 45 L120 45" fill="#A5B4FC"/>
      <circle cx="50" cy="50" r="20" fill="none" stroke="#6366F1" stroke-width="2" stroke-dasharray="4"/>
      <line x1="50" y1="50" x2="70" y2="70" stroke="#6366F1" stroke-width="2"/>
      <circle cx="70" cy="70" r="5" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-target',
    type: 'illustration',
    name: 'Ziel erreichen',
    tags: ['ziel', 'target', 'erfolg', 'pfeil', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="75" r="50" fill="#E5E7EB"/>
      <circle cx="100" cy="75" r="38" fill="white"/>
      <circle cx="100" cy="75" r="26" fill="#C7D2FE"/>
      <circle cx="100" cy="75" r="14" fill="#6366F1"/>
      <line x1="30" y1="45" x2="95" y2="72" stroke="#6366F1" stroke-width="4"/>
      <polygon points="95,72 85,65 88,78" fill="#6366F1"/>
      <line x1="20" y1="50" x2="30" y2="45" stroke="#6366F1" stroke-width="3"/>
      <line x1="25" y1="55" x2="30" y2="45" stroke="#6366F1" stroke-width="3"/>
      <line x1="20" y1="40" x2="30" y2="45" stroke="#6366F1" stroke-width="3"/>
    </svg>`
  },
  {
    id: 'illus-deal',
    type: 'illustration',
    name: 'Deal abschliessen',
    tags: ['deal', 'handschlag', 'vertrag', 'partnerschaft', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="75" r="55" fill="#E5E7EB"/>
      <circle cx="100" cy="75" r="48" fill="white"/>
      <path d="M55 80 L75 65 L85 75 L100 60 L115 75 L125 65 L145 80" stroke="#6366F1" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      <path d="M70 90 L75 80 L95 80 Q100 85 105 80 L125 80 L130 90" fill="#FCD9BD"/>
      <circle cx="60" cy="50" r="12" fill="#6366F1"/>
      <circle cx="140" cy="50" r="12" fill="#A5B4FC"/>
    </svg>`
  },
  {
    id: 'illus-brainstorm',
    type: 'illustration',
    name: 'Brainstorming',
    tags: ['brainstorm', 'idee', 'kreativ', 'team', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="75" r="40" fill="#E5E7EB"/>
      <circle cx="100" cy="75" r="32" fill="white"/>
      <circle cx="50" cy="45" r="15" fill="#6366F1"/>
      <circle cx="150" cy="45" r="15" fill="#A5B4FC"/>
      <circle cx="50" cy="105" r="15" fill="#C7D2FE"/>
      <circle cx="150" cy="105" r="15" fill="#6366F1"/>
      <line x1="65" y1="50" x2="85" y2="65" stroke="#6366F1" stroke-width="2" stroke-dasharray="4"/>
      <line x1="135" y1="50" x2="115" y2="65" stroke="#A5B4FC" stroke-width="2" stroke-dasharray="4"/>
      <line x1="65" y1="100" x2="85" y2="85" stroke="#C7D2FE" stroke-width="2" stroke-dasharray="4"/>
      <line x1="135" y1="100" x2="115" y2="85" stroke="#6366F1" stroke-width="2" stroke-dasharray="4"/>
      <polygon points="100,55 110,75 100,70 90,75" fill="#FBBF24"/>
      <text x="92" y="95" fill="#6366F1" font-size="16" font-weight="bold">?</text>
    </svg>`
  },
  {
    id: 'illus-startup',
    type: 'illustration',
    name: 'Startup',
    tags: ['startup', 'rakete', 'launch', 'gruendung', 'business'],
    category: 'business',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="100" cy="130" rx="40" ry="10" fill="#E5E7EB"/>
      <path d="M100 20 L120 80 L110 80 L110 110 L90 110 L90 80 L80 80 Z" fill="#6366F1"/>
      <circle cx="100" cy="55" r="10" fill="#A5B4FC"/>
      <circle cx="100" cy="55" r="5" fill="white"/>
      <polygon points="80,80 70,95 80,90" fill="#A5B4FC"/>
      <polygon points="120,80 130,95 120,90" fill="#A5B4FC"/>
      <path d="M95 110 L90 130 L100 125 L110 130 L105 110" fill="#F59E0B"/>
      <circle cx="60" cy="40" r="4" fill="#FBBF24"/>
      <circle cx="140" cy="50" r="3" fill="#FBBF24"/>
      <circle cx="150" cy="30" r="2" fill="#FBBF24"/>
    </svg>`
  },
];

// ===== TECHNOLOGY ILLUSTRATIONEN =====
const technologyIllustrations: IllustrationElement[] = [
  {
    id: 'illus-coding',
    type: 'illustration',
    name: 'Programmieren',
    tags: ['coding', 'programmieren', 'code', 'entwicklung', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="30" width="140" height="90" rx="8" fill="#1F2937"/>
      <rect x="35" y="35" width="130" height="80" rx="4" fill="#111827"/>
      <text x="45" y="55" fill="#6366F1" font-family="monospace" font-size="10">const</text>
      <text x="70" y="55" fill="#A5B4FC" font-family="monospace" font-size="10">data</text>
      <text x="95" y="55" fill="white" font-family="monospace" font-size="10">= []</text>
      <text x="45" y="70" fill="#6366F1" font-family="monospace" font-size="10">function</text>
      <text x="95" y="70" fill="#34D399" font-family="monospace" font-size="10">run()</text>
      <text x="55" y="85" fill="#A5B4FC" font-family="monospace" font-size="10">return</text>
      <text x="95" y="85" fill="white" font-family="monospace" font-size="10">true</text>
      <rect x="45" y="95" width="8" height="12" fill="#6366F1"/>
      <rect x="90" y="125" width="20" height="5" rx="1" fill="#4B5563"/>
    </svg>`
  },
  {
    id: 'illus-devices',
    type: 'illustration',
    name: 'Geraete',
    tags: ['geraete', 'devices', 'laptop', 'smartphone', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="40" width="120" height="75" rx="4" fill="#374151"/>
      <rect x="25" y="45" width="110" height="65" rx="2" fill="#6366F1"/>
      <rect x="30" y="50" width="100" height="55" fill="white"/>
      <rect x="10" y="115" width="140" height="8" rx="2" fill="#4B5563"/>
      <rect x="145" y="55" width="40" height="75" rx="4" fill="#374151"/>
      <rect x="148" y="60" width="34" height="60" rx="2" fill="white"/>
      <rect x="155" y="65" width="20" height="8" fill="#A5B4FC"/>
      <rect x="155" y="78" width="20" height="5" fill="#E5E7EB"/>
      <rect x="155" y="86" width="20" height="5" fill="#E5E7EB"/>
      <circle cx="165" cy="123" r="4" fill="#6B7280"/>
      <rect x="40" y="60" width="30" height="20" rx="2" fill="#6366F1"/>
      <rect x="40" y="85" width="80" height="8" rx="1" fill="#E5E7EB"/>
      <rect x="40" y="96" width="60" height="5" rx="1" fill="#E5E7EB"/>
    </svg>`
  },
  {
    id: 'illus-cloud',
    type: 'illustration',
    name: 'Cloud Computing',
    tags: ['cloud', 'server', 'daten', 'speicher', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M150 70 Q170 70 170 50 Q170 30 145 30 Q140 15 115 15 Q85 15 80 40 Q55 40 55 60 Q55 80 80 80 L150 80 Q170 80 170 70 Q170 70 150 70" fill="#6366F1"/>
      <rect x="70" y="95" width="60" height="40" rx="4" fill="#374151"/>
      <line x1="80" y1="105" x2="90" y2="105" stroke="#6366F1" stroke-width="2"/>
      <line x1="80" y1="112" x2="95" y2="112" stroke="#A5B4FC" stroke-width="2"/>
      <line x1="80" y1="119" x2="88" y2="119" stroke="#6366F1" stroke-width="2"/>
      <circle cx="115" cy="115" r="8" fill="#6366F1"/>
      <line x1="100" y1="80" x2="100" y2="95" stroke="#6366F1" stroke-width="3" stroke-dasharray="4"/>
      <polygon points="96,90 100,98 104,90" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-ai',
    type: 'illustration',
    name: 'Kuenstliche Intelligenz',
    tags: ['ki', 'ai', 'roboter', 'intelligent', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="60" r="40" fill="#E5E7EB"/>
      <circle cx="100" cy="60" r="35" fill="white"/>
      <circle cx="85" cy="55" r="8" fill="#6366F1"/>
      <circle cx="115" cy="55" r="8" fill="#6366F1"/>
      <circle cx="85" cy="55" r="3" fill="white"/>
      <circle cx="115" cy="55" r="3" fill="white"/>
      <path d="M85 75 Q100 85 115 75" stroke="#6366F1" stroke-width="3" fill="none" stroke-linecap="round"/>
      <rect x="90" y="100" width="20" height="8" rx="2" fill="#6366F1"/>
      <rect x="80" y="108" width="40" height="25" rx="4" fill="#374151"/>
      <line x1="70" y1="40" x2="55" y2="25" stroke="#6366F1" stroke-width="2"/>
      <circle cx="55" cy="25" r="5" fill="#6366F1"/>
      <line x1="130" y1="40" x2="145" y2="25" stroke="#6366F1" stroke-width="2"/>
      <circle cx="145" cy="25" r="5" fill="#6366F1"/>
      <line x1="100" y1="20" x2="100" y2="5" stroke="#6366F1" stroke-width="2"/>
      <circle cx="100" cy="5" r="5" fill="#A5B4FC"/>
    </svg>`
  },
  {
    id: 'illus-security',
    type: 'illustration',
    name: 'Sicherheit',
    tags: ['sicherheit', 'security', 'schloss', 'schutz', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M100 15 L160 35 L160 75 Q160 115 100 135 Q40 115 40 75 L40 35 Z" fill="#6366F1"/>
      <path d="M100 25 L150 42 L150 75 Q150 108 100 125 Q50 108 50 75 L50 42 Z" fill="white"/>
      <circle cx="100" cy="70" r="20" fill="#6366F1"/>
      <rect x="95" y="85" width="10" height="20" rx="2" fill="#6366F1"/>
      <circle cx="100" cy="70" r="8" fill="white"/>
      <path d="M85 60 L85 50 Q85 40 100 40 Q115 40 115 50 L115 60" stroke="#6366F1" stroke-width="4" fill="none"/>
    </svg>`
  },
  {
    id: 'illus-network',
    type: 'illustration',
    name: 'Netzwerk',
    tags: ['netzwerk', 'network', 'verbindung', 'nodes', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="100" y1="75" x2="50" y2="40" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="100" y1="75" x2="150" y2="40" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="100" y1="75" x2="50" y2="110" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="100" y1="75" x2="150" y2="110" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="50" y1="40" x2="150" y2="40" stroke="#E5E7EB" stroke-width="2"/>
      <line x1="50" y1="110" x2="150" y2="110" stroke="#E5E7EB" stroke-width="2"/>
      <circle cx="100" cy="75" r="15" fill="#6366F1"/>
      <circle cx="50" cy="40" r="12" fill="#A5B4FC"/>
      <circle cx="150" cy="40" r="12" fill="#A5B4FC"/>
      <circle cx="50" cy="110" r="12" fill="#A5B4FC"/>
      <circle cx="150" cy="110" r="12" fill="#A5B4FC"/>
      <circle cx="100" cy="75" r="6" fill="white"/>
    </svg>`
  },
  {
    id: 'illus-app',
    type: 'illustration',
    name: 'Mobile App',
    tags: ['app', 'mobile', 'smartphone', 'entwicklung', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="65" y="15" width="70" height="120" rx="8" fill="#374151"/>
      <rect x="70" y="25" width="60" height="100" rx="4" fill="white"/>
      <rect x="80" y="35" width="40" height="25" rx="4" fill="#6366F1"/>
      <rect x="80" y="65" width="18" height="18" rx="4" fill="#A5B4FC"/>
      <rect x="102" y="65" width="18" height="18" rx="4" fill="#C7D2FE"/>
      <rect x="80" y="87" width="18" height="18" rx="4" fill="#C7D2FE"/>
      <rect x="102" y="87" width="18" height="18" rx="4" fill="#A5B4FC"/>
      <rect x="90" y="110" width="20" height="8" rx="4" fill="#E5E7EB"/>
      <circle cx="100" cy="20" r="2" fill="#6B7280"/>
    </svg>`
  },
  {
    id: 'illus-data',
    type: 'illustration',
    name: 'Datenbank',
    tags: ['daten', 'database', 'speicher', 'server', 'technology'],
    category: 'technology',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="100" cy="35" rx="50" ry="15" fill="#6366F1"/>
      <path d="M50 35 L50 65 Q50 80 100 80 Q150 80 150 65 L150 35" fill="#A5B4FC"/>
      <ellipse cx="100" cy="65" rx="50" ry="15" fill="#6366F1"/>
      <path d="M50 65 L50 95 Q50 110 100 110 Q150 110 150 95 L150 65" fill="#A5B4FC"/>
      <ellipse cx="100" cy="95" rx="50" ry="15" fill="#6366F1"/>
      <path d="M50 95 L50 115 Q50 130 100 130 Q150 130 150 115 L150 95" fill="#C7D2FE"/>
      <ellipse cx="100" cy="115" rx="50" ry="15" fill="#6366F1"/>
    </svg>`
  },
];

// ===== PEOPLE ILLUSTRATIONEN =====
const peopleIllustrations: IllustrationElement[] = [
  {
    id: 'illus-working',
    type: 'illustration',
    name: 'Am Arbeiten',
    tags: ['arbeiten', 'laptop', 'homeoffice', 'produktiv', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="60" y="80" width="80" height="50" rx="4" fill="#374151"/>
      <rect x="65" y="85" width="70" height="40" rx="2" fill="#6366F1"/>
      <rect x="50" y="130" width="100" height="5" rx="1" fill="#4B5563"/>
      <circle cx="100" cy="45" r="20" fill="#FCD9BD"/>
      <path d="M80 45 Q80 25 100 25 Q120 25 120 45" fill="#1F2937"/>
      <circle cx="92" cy="42" r="3" fill="#1F2937"/>
      <circle cx="108" cy="42" r="3" fill="#1F2937"/>
      <path d="M95 52 Q100 56 105 52" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M70 130 L70 75 Q100 60 130 75 L130 130" fill="#6366F1"/>
      <rect x="85" y="75" width="30" height="25" fill="#FCD9BD"/>
    </svg>`
  },
  {
    id: 'illus-relaxing',
    type: 'illustration',
    name: 'Entspannen',
    tags: ['entspannen', 'pause', 'couch', 'freizeit', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="80" width="140" height="50" rx="8" fill="#6366F1"/>
      <rect x="25" y="90" width="20" height="40" rx="4" fill="#4F46E5"/>
      <rect x="155" y="90" width="20" height="40" rx="4" fill="#4F46E5"/>
      <circle cx="100" cy="55" r="18" fill="#FCD9BD"/>
      <path d="M82 55 Q82 37 100 37 Q118 37 118 55" fill="#1F2937"/>
      <circle cx="93" cy="52" r="2" fill="#1F2937"/>
      <circle cx="107" cy="52" r="2" fill="#1F2937"/>
      <path d="M95 62 Q100 65 105 62" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M75 95 L85 70 Q100 60 115 70 L125 95" fill="#A5B4FC"/>
      <path d="M70 100 L75 95 L85 105 L80 110 Z" fill="#FCD9BD"/>
      <path d="M130 100 L125 95 L115 105 L120 110 Z" fill="#FCD9BD"/>
      <rect x="60" y="105" width="80" height="25" fill="#A5B4FC"/>
    </svg>`
  },
  {
    id: 'illus-celebrating',
    type: 'illustration',
    name: 'Feiern',
    tags: ['feiern', 'party', 'erfolg', 'gluecklich', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="50" r="20" fill="#FCD9BD"/>
      <path d="M80 50 Q80 30 100 30 Q120 30 120 50" fill="#1F2937"/>
      <circle cx="93" cy="47" r="3" fill="#1F2937"/>
      <circle cx="107" cy="47" r="3" fill="#1F2937"/>
      <path d="M90 58 Q100 68 110 58" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M75 135 L80 75 Q100 65 120 75 L125 135" fill="#6366F1"/>
      <path d="M80 80 L55 55" stroke="#FCD9BD" stroke-width="8" stroke-linecap="round"/>
      <path d="M120 80 L145 55" stroke="#FCD9BD" stroke-width="8" stroke-linecap="round"/>
      <polygon points="50,20 52,28 60,28 54,33 56,42 50,37 44,42 46,33 40,28 48,28" fill="#FBBF24"/>
      <polygon points="150,25 152,33 160,33 154,38 156,47 150,42 144,47 146,38 140,33 148,33" fill="#FBBF24"/>
      <polygon points="100,10 102,18 110,18 104,23 106,32 100,27 94,32 96,23 90,18 98,18" fill="#FBBF24"/>
      <rect x="40" y="45" width="5" height="12" fill="#EC4899" transform="rotate(-20 42 51)"/>
      <rect x="155" y="40" width="5" height="12" fill="#34D399" transform="rotate(20 157 46)"/>
    </svg>`
  },
  {
    id: 'illus-thinking',
    type: 'illustration',
    name: 'Nachdenken',
    tags: ['denken', 'idee', 'kreativ', 'brainstorm', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="90" cy="70" r="25" fill="#FCD9BD"/>
      <path d="M65 70 Q65 45 90 45 Q115 45 115 70" fill="#1F2937"/>
      <circle cx="82" cy="67" r="3" fill="#1F2937"/>
      <circle cx="98" cy="67" r="3" fill="#1F2937"/>
      <path d="M82 80 Q90 85 98 80" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M60 140 L70 100 Q90 90 110 100 L120 140" fill="#6366F1"/>
      <path d="M70 105 L55 95 L50 105" stroke="#FCD9BD" stroke-width="8" stroke-linecap="round" fill="none"/>
      <circle cx="145" cy="40" r="20" fill="#FBBF24"/>
      <text x="138" y="48" fill="white" font-size="24" font-weight="bold">!</text>
      <circle cx="125" cy="55" r="5" fill="#FDE68A"/>
      <circle cx="118" cy="65" r="3" fill="#FDE68A"/>
    </svg>`
  },
  {
    id: 'illus-running',
    type: 'illustration',
    name: 'Laufen',
    tags: ['laufen', 'sport', 'fitness', 'bewegung', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="90" cy="35" r="18" fill="#FCD9BD"/>
      <path d="M72 35 Q72 17 90 17 Q108 17 108 35" fill="#1F2937"/>
      <circle cx="84" cy="33" r="2" fill="#1F2937"/>
      <circle cx="96" cy="33" r="2" fill="#1F2937"/>
      <path d="M100 55 L115 85" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <path d="M80 55 L60 75" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <ellipse cx="90" cy="70" rx="20" ry="25" fill="#6366F1"/>
      <path d="M85 95 L70 130" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <path d="M95 95 L120 120" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <ellipse cx="65" cy="135" rx="10" ry="5" fill="#374151"/>
      <ellipse cx="125" cy="125" rx="10" ry="5" fill="#374151"/>
      <line x1="30" y1="140" x2="170" y2="140" stroke="#E5E7EB" stroke-width="3"/>
    </svg>`
  },
  {
    id: 'illus-reading',
    type: 'illustration',
    name: 'Lesen',
    tags: ['lesen', 'buch', 'lernen', 'bildung', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="100" width="140" height="40" rx="4" fill="#8B5CF6"/>
      <circle cx="70" cy="55" r="22" fill="#FCD9BD"/>
      <path d="M48 55 Q48 33 70 33 Q92 33 92 55" fill="#1F2937"/>
      <circle cx="62" cy="52" r="3" fill="#1F2937"/>
      <circle cx="78" cy="52" r="3" fill="#1F2937"/>
      <path d="M65 65 Q70 68 75 65" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M50 110 L55 80 Q70 72 85 80 L90 110" fill="#6366F1"/>
      <rect x="100" y="55" width="60" height="45" rx="2" fill="#FEF3C7"/>
      <rect x="100" y="55" width="30" height="45" fill="#FDE68A"/>
      <line x1="110" y1="65" x2="125" y2="65" stroke="#D97706" stroke-width="2"/>
      <line x1="110" y1="75" x2="120" y2="75" stroke="#D97706" stroke-width="2"/>
      <line x1="135" y1="65" x2="150" y2="65" stroke="#D97706" stroke-width="2"/>
      <line x1="135" y1="75" x2="155" y2="75" stroke="#D97706" stroke-width="2"/>
      <path d="M90 85 L100 75" stroke="#FCD9BD" stroke-width="6" stroke-linecap="round"/>
      <path d="M90 85 L100 95" stroke="#FCD9BD" stroke-width="6" stroke-linecap="round"/>
    </svg>`
  },
  {
    id: 'illus-meditating',
    type: 'illustration',
    name: 'Meditieren',
    tags: ['meditation', 'yoga', 'ruhe', 'achtsamkeit', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="100" cy="130" rx="60" ry="10" fill="#E5E7EB"/>
      <circle cx="100" cy="50" r="22" fill="#FCD9BD"/>
      <path d="M78 50 Q78 28 100 28 Q122 28 122 50" fill="#1F2937"/>
      <line x1="90" y1="47" x2="95" y2="47" stroke="#1F2937" stroke-width="2"/>
      <line x1="105" y1="47" x2="110" y2="47" stroke="#1F2937" stroke-width="2"/>
      <path d="M95 58 Q100 62 105 58" stroke="#1F2937" stroke-width="2" fill="none"/>
      <ellipse cx="100" cy="100" rx="35" ry="30" fill="#6366F1"/>
      <path d="M65 95 L50 85 Q45 90 55 100" fill="#FCD9BD"/>
      <path d="M135 95 L150 85 Q155 90 145 100" fill="#FCD9BD"/>
      <path d="M75 120 Q100 140 125 120" fill="#6366F1"/>
      <circle cx="55" cy="30" r="3" fill="#A5B4FC"/>
      <circle cx="145" cy="30" r="3" fill="#A5B4FC"/>
      <circle cx="100" cy="15" r="4" fill="#A5B4FC"/>
      <circle cx="70" cy="20" r="2" fill="#C7D2FE"/>
      <circle cx="130" cy="20" r="2" fill="#C7D2FE"/>
    </svg>`
  },
  {
    id: 'illus-coffee',
    type: 'illustration',
    name: 'Kaffee trinken',
    tags: ['kaffee', 'pause', 'gemuetlich', 'entspannung', 'people'],
    category: 'people',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="80" cy="50" r="22" fill="#FCD9BD"/>
      <path d="M58 50 Q58 28 80 28 Q102 28 102 50" fill="#1F2937"/>
      <circle cx="72" cy="47" r="3" fill="#1F2937"/>
      <circle cx="88" cy="47" r="3" fill="#1F2937"/>
      <path d="M75 58 Q80 62 85 58" stroke="#1F2937" stroke-width="2" fill="none"/>
      <path d="M55 130 L65 80 Q80 72 95 80 L105 130" fill="#6366F1"/>
      <rect x="115" y="75" width="50" height="40" rx="4" fill="white" stroke="#D1D5DB" stroke-width="2"/>
      <path d="M165 85 Q175 85 175 95 Q175 105 165 105" stroke="#D1D5DB" stroke-width="2" fill="none"/>
      <rect x="120" y="80" width="40" height="10" fill="#7C3AED"/>
      <path d="M125 70 Q130 60 135 70" stroke="#9CA3AF" stroke-width="2" fill="none"/>
      <path d="M140" y1="68" y2="Q145 58 150 68" stroke="#9CA3AF" stroke-width="2" fill="none"/>
      <path d="M100 90 L115 85" stroke="#FCD9BD" stroke-width="8" stroke-linecap="round"/>
      <line x1="30" y1="130" x2="170" y2="130" stroke="#E5E7EB" stroke-width="3"/>
    </svg>`
  },
];

// ===== NATURE ILLUSTRATIONEN =====
const natureIllustrations: IllustrationElement[] = [
  {
    id: 'illus-plant',
    type: 'illustration',
    name: 'Pflanze',
    tags: ['pflanze', 'topfpflanze', 'gruen', 'natur', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="70" y="95" width="60" height="45" rx="4" fill="#6366F1"/>
      <rect x="75" y="100" width="50" height="35" rx="2" fill="#4F46E5"/>
      <path d="M100 95 L100 70" stroke="#059669" stroke-width="4"/>
      <ellipse cx="80" cy="55" rx="20" ry="25" fill="#10B981"/>
      <ellipse cx="120" cy="55" rx="20" ry="25" fill="#10B981"/>
      <ellipse cx="100" cy="45" rx="18" ry="22" fill="#34D399"/>
      <ellipse cx="90" cy="60" rx="12" ry="15" fill="#059669"/>
      <ellipse cx="110" cy="60" rx="12" ry="15" fill="#059669"/>
      <line x1="100" y1="95" x2="100" y2="75" stroke="#065F46" stroke-width="3"/>
    </svg>`
  },
  {
    id: 'illus-tree',
    type: 'illustration',
    name: 'Baum',
    tags: ['baum', 'wald', 'natur', 'oekologie', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="90" y="95" width="20" height="45" fill="#92400E"/>
      <ellipse cx="100" cy="60" rx="45" ry="50" fill="#10B981"/>
      <ellipse cx="70" cy="70" rx="25" ry="30" fill="#059669"/>
      <ellipse cx="130" cy="70" rx="25" ry="30" fill="#059669"/>
      <ellipse cx="100" cy="40" rx="30" ry="35" fill="#34D399"/>
      <line x1="30" y1="140" x2="170" y2="140" stroke="#D1D5DB" stroke-width="3"/>
      <circle cx="50" cy="130" r="8" fill="#6366F1" opacity="0.5"/>
      <circle cx="150" cy="130" r="8" fill="#6366F1" opacity="0.5"/>
    </svg>`
  },
  {
    id: 'illus-mountain',
    type: 'illustration',
    name: 'Berg',
    tags: ['berg', 'landschaft', 'natur', 'wandern', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="150" fill="#E0F2FE"/>
      <circle cx="160" cy="35" r="20" fill="#FDE68A"/>
      <polygon points="100,30 160,130 40,130" fill="#6366F1"/>
      <polygon points="100,30 120,70 80,70" fill="white"/>
      <polygon points="50,60 100,130 0,130" fill="#A5B4FC"/>
      <polygon points="150,70 200,130 100,130" fill="#818CF8"/>
      <rect x="0" y="125" width="200" height="25" fill="#10B981"/>
    </svg>`
  },
  {
    id: 'illus-sun',
    type: 'illustration',
    name: 'Sonne',
    tags: ['sonne', 'wetter', 'sonnig', 'tag', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="75" r="35" fill="#FBBF24"/>
      <line x1="100" y1="20" x2="100" y2="5" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="100" y1="145" x2="100" y2="130" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="155" y1="75" x2="170" y2="75" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="30" y1="75" x2="45" y2="75" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="140" y1="35" x2="150" y2="25" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="50" y1="125" x2="60" y2="115" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="140" y1="115" x2="150" y2="125" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <line x1="50" y1="25" x2="60" y2="35" stroke="#FCD34D" stroke-width="6" stroke-linecap="round"/>
      <circle cx="100" cy="75" r="25" fill="#FDE68A"/>
      <path d="M88 68 Q100 80 112 68" stroke="#F59E0B" stroke-width="3" fill="none"/>
    </svg>`
  },
  {
    id: 'illus-flower',
    type: 'illustration',
    name: 'Blume',
    tags: ['blume', 'natur', 'fruehling', 'garten', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M100 140 L100 80" stroke="#10B981" stroke-width="5"/>
      <path d="M100 100 Q80 90 70 100" stroke="#10B981" stroke-width="4" fill="none"/>
      <ellipse cx="70" cy="100" rx="10" ry="5" fill="#34D399"/>
      <ellipse cx="100" cy="50" rx="15" ry="20" fill="#6366F1"/>
      <ellipse cx="130" cy="65" rx="15" ry="20" fill="#A5B4FC" transform="rotate(45 130 65)"/>
      <ellipse cx="70" cy="65" rx="15" ry="20" fill="#A5B4FC" transform="rotate(-45 70 65)"/>
      <ellipse cx="120" cy="40" rx="15" ry="20" fill="#6366F1" transform="rotate(30 120 40)"/>
      <ellipse cx="80" cy="40" rx="15" ry="20" fill="#6366F1" transform="rotate(-30 80 40)"/>
      <circle cx="100" cy="55" r="12" fill="#FBBF24"/>
      <circle cx="100" cy="55" r="6" fill="#F59E0B"/>
    </svg>`
  },
  {
    id: 'illus-rain',
    type: 'illustration',
    name: 'Regen',
    tags: ['regen', 'wetter', 'wolke', 'tropfen', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M150 55 Q175 55 175 35 Q175 15 145 15 Q140 0 110 0 Q75 0 70 25 Q40 25 40 50 Q40 70 70 70 L150 70 Q175 70 175 55" fill="#94A3B8"/>
      <line x1="60" y1="85" x2="50" y2="105" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <line x1="85" y1="85" x2="75" y2="115" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <line x1="110" y1="85" x2="100" y2="105" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <line x1="135" y1="85" x2="125" y2="115" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <line x1="70" y1="115" x2="60" y2="135" stroke="#A5B4FC" stroke-width="3" stroke-linecap="round"/>
      <line x1="120" y1="115" x2="110" y2="135" stroke="#A5B4FC" stroke-width="3" stroke-linecap="round"/>
    </svg>`
  },
  {
    id: 'illus-leaf',
    type: 'illustration',
    name: 'Blatt',
    tags: ['blatt', 'natur', 'oeko', 'gruen', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M100 130 Q70 100 100 20 Q130 100 100 130" fill="#10B981"/>
      <path d="M100 130 L100 40" stroke="#059669" stroke-width="3"/>
      <path d="M100 60 Q120 70 130 50" stroke="#059669" stroke-width="2" fill="none"/>
      <path d="M100 80 Q80 90 70 70" stroke="#059669" stroke-width="2" fill="none"/>
      <path d="M100 100 Q115 105 125 90" stroke="#059669" stroke-width="2" fill="none"/>
      <circle cx="50" cy="120" r="4" fill="#6366F1"/>
      <circle cx="150" cy="110" r="3" fill="#A5B4FC"/>
      <circle cx="160" cy="130" r="5" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-ocean',
    type: 'illustration',
    name: 'Ozean',
    tags: ['ozean', 'meer', 'wellen', 'wasser', 'nature'],
    category: 'nature',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="200" height="60" fill="#FDE68A"/>
      <circle cx="170" cy="30" r="20" fill="#FBBF24"/>
      <rect x="0" y="60" width="200" height="90" fill="#6366F1"/>
      <path d="M0 70 Q25 60 50 70 T100 70 T150 70 T200 70 L200 90 Q175 80 150 90 T100 90 T50 90 T0 90 Z" fill="#818CF8"/>
      <path d="M0 95 Q25 85 50 95 T100 95 T150 95 T200 95 L200 115 Q175 105 150 115 T100 115 T50 115 T0 115 Z" fill="#A5B4FC"/>
      <path d="M0 120 Q25 110 50 120 T100 120 T150 120 T200 120 L200 150 L0 150 Z" fill="#C7D2FE"/>
    </svg>`
  },
];

// ===== ABSTRACT ILLUSTRATIONEN =====
const abstractIllustrations: IllustrationElement[] = [
  {
    id: 'illus-circles',
    type: 'illustration',
    name: 'Kreise',
    tags: ['kreise', 'abstrakt', 'muster', 'design', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="60" cy="50" r="35" fill="#6366F1"/>
      <circle cx="100" cy="80" r="40" fill="#A5B4FC"/>
      <circle cx="140" cy="50" r="30" fill="#C7D2FE"/>
      <circle cx="80" cy="100" r="25" fill="#818CF8"/>
      <circle cx="150" cy="100" r="35" fill="#6366F1" opacity="0.5"/>
      <circle cx="40" cy="90" r="20" fill="#E0E7FF"/>
    </svg>`
  },
  {
    id: 'illus-waves',
    type: 'illustration',
    name: 'Wellen',
    tags: ['wellen', 'abstrakt', 'muster', 'flow', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 80 Q50 40 100 80 T200 80 L200 150 L0 150 Z" fill="#6366F1"/>
      <path d="M0 95 Q50 55 100 95 T200 95 L200 150 L0 150 Z" fill="#818CF8"/>
      <path d="M0 110 Q50 70 100 110 T200 110 L200 150 L0 150 Z" fill="#A5B4FC"/>
      <path d="M0 125 Q50 85 100 125 T200 125 L200 150 L0 150 Z" fill="#C7D2FE"/>
    </svg>`
  },
  {
    id: 'illus-blob',
    type: 'illustration',
    name: 'Blob Form',
    tags: ['blob', 'abstrakt', 'form', 'organisch', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M100 20 Q150 10 170 50 Q190 90 150 120 Q110 150 60 130 Q10 110 20 70 Q30 30 100 20" fill="#6366F1"/>
      <path d="M90 35 Q130 30 145 60 Q160 90 130 110 Q100 130 60 115 Q25 100 35 70 Q45 40 90 35" fill="#A5B4FC"/>
    </svg>`
  },
  {
    id: 'illus-dots-pattern',
    type: 'illustration',
    name: 'Punktmuster',
    tags: ['punkte', 'muster', 'abstrakt', 'pattern', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="30" cy="30" r="8" fill="#6366F1"/>
      <circle cx="70" cy="30" r="8" fill="#A5B4FC"/>
      <circle cx="110" cy="30" r="8" fill="#6366F1"/>
      <circle cx="150" cy="30" r="8" fill="#A5B4FC"/>
      <circle cx="50" cy="60" r="8" fill="#A5B4FC"/>
      <circle cx="90" cy="60" r="8" fill="#6366F1"/>
      <circle cx="130" cy="60" r="8" fill="#A5B4FC"/>
      <circle cx="170" cy="60" r="8" fill="#6366F1"/>
      <circle cx="30" cy="90" r="8" fill="#6366F1"/>
      <circle cx="70" cy="90" r="8" fill="#A5B4FC"/>
      <circle cx="110" cy="90" r="8" fill="#6366F1"/>
      <circle cx="150" cy="90" r="8" fill="#A5B4FC"/>
      <circle cx="50" cy="120" r="8" fill="#A5B4FC"/>
      <circle cx="90" cy="120" r="8" fill="#6366F1"/>
      <circle cx="130" cy="120" r="8" fill="#A5B4FC"/>
      <circle cx="170" cy="120" r="8" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-gradient-shape',
    type: 'illustration',
    name: 'Gradient Form',
    tags: ['gradient', 'verlauf', 'abstrakt', 'modern', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#6366F1"/>
          <stop offset="100%" stop-color="#A855F7"/>
        </linearGradient>
      </defs>
      <rect x="20" y="20" width="160" height="110" rx="20" fill="url(#grad1)"/>
      <circle cx="60" cy="60" r="25" fill="white" opacity="0.2"/>
      <circle cx="150" cy="100" r="35" fill="white" opacity="0.15"/>
    </svg>`
  },
  {
    id: 'illus-lines-abstract',
    type: 'illustration',
    name: 'Linien Abstrakt',
    tags: ['linien', 'abstrakt', 'geometrisch', 'modern', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'outline',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="20" y1="130" x2="60" y2="20" stroke="#6366F1" stroke-width="3"/>
      <line x1="50" y1="130" x2="90" y2="20" stroke="#818CF8" stroke-width="3"/>
      <line x1="80" y1="130" x2="120" y2="20" stroke="#A5B4FC" stroke-width="3"/>
      <line x1="110" y1="130" x2="150" y2="20" stroke="#C7D2FE" stroke-width="3"/>
      <line x1="140" y1="130" x2="180" y2="20" stroke="#E0E7FF" stroke-width="3"/>
      <circle cx="60" cy="20" r="8" fill="#6366F1"/>
      <circle cx="120" cy="20" r="8" fill="#A5B4FC"/>
      <circle cx="50" cy="130" r="8" fill="#818CF8"/>
      <circle cx="140" cy="130" r="8" fill="#C7D2FE"/>
    </svg>`
  },
  {
    id: 'illus-hexagon-pattern',
    type: 'illustration',
    name: 'Hexagon Muster',
    tags: ['hexagon', 'wabe', 'muster', 'geometrisch', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="50,35 70,20 90,35 90,60 70,75 50,60" fill="#6366F1"/>
      <polygon points="90,35 110,20 130,35 130,60 110,75 90,60" fill="#A5B4FC"/>
      <polygon points="130,35 150,20 170,35 170,60 150,75 130,60" fill="#C7D2FE"/>
      <polygon points="30,70 50,55 70,70 70,95 50,110 30,95" fill="#818CF8"/>
      <polygon points="70,70 90,55 110,70 110,95 90,110 70,95" fill="#6366F1"/>
      <polygon points="110,70 130,55 150,70 150,95 130,110 110,95" fill="#A5B4FC"/>
      <polygon points="150,70 170,55 190,70 190,95 170,110 150,95" fill="#E0E7FF"/>
      <polygon points="50,105 70,90 90,105 90,130 70,145 50,130" fill="#C7D2FE"/>
      <polygon points="90,105 110,90 130,105 130,130 110,145 90,130" fill="#818CF8"/>
      <polygon points="130,105 150,90 170,105 170,130 150,145 130,130" fill="#6366F1"/>
    </svg>`
  },
  {
    id: 'illus-burst',
    type: 'illustration',
    name: 'Strahlen',
    tags: ['strahlen', 'burst', 'abstrakt', 'energie', 'abstract'],
    category: 'abstract',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="100,0 105,60 110,0 105,60 120,5 105,60 130,15 105,60 140,30 105,60 145,45 105,60 150,60 105,60" fill="#C7D2FE"/>
      <polygon points="100,150 105,90 110,150 105,90 120,145 105,90 130,135 105,90 140,120 105,90 145,105 105,90 150,90 105,90" fill="#C7D2FE"/>
      <polygon points="0,75 60,70 0,65 60,70 5,55 60,70 15,45 60,70 30,35 60,70 45,30 60,70 60,25 60,70" fill="#A5B4FC"/>
      <polygon points="200,75 140,70 200,65 140,70 195,55 140,70 185,45 140,70 170,35 140,70 155,30 140,70 140,25 140,70" fill="#A5B4FC"/>
      <circle cx="100" cy="75" r="30" fill="#6366F1"/>
      <circle cx="100" cy="75" r="18" fill="#818CF8"/>
    </svg>`
  },
];

// ===== SOCIAL MEDIA ILLUSTRATIONEN =====
const socialMediaIllustrations: IllustrationElement[] = [
  {
    id: 'illus-followers',
    type: 'illustration',
    name: 'Follower',
    tags: ['follower', 'abonnenten', 'community', 'social', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="45" r="25" fill="#6366F1"/>
      <circle cx="100" cy="40" r="12" fill="#FCD9BD"/>
      <path d="M75 90 L75 70 Q100 55 125 70 L125 90" fill="#6366F1"/>
      <circle cx="55" cy="60" r="18" fill="#A5B4FC"/>
      <circle cx="55" cy="56" r="9" fill="#FCD9BD"/>
      <path d="M38 95 L38 80 Q55 70 72 80 L72 95" fill="#A5B4FC"/>
      <circle cx="145" cy="60" r="18" fill="#A5B4FC"/>
      <circle cx="145" cy="56" r="9" fill="#FCD9BD"/>
      <path d="M128 95 L128 80 Q145 70 162 80 L162 95" fill="#A5B4FC"/>
      <circle cx="35" cy="85" r="12" fill="#C7D2FE"/>
      <circle cx="35" cy="82" r="6" fill="#FCD9BD"/>
      <path d="M23 115 L23 100 Q35 93 47 100 L47 115" fill="#C7D2FE"/>
      <circle cx="165" cy="85" r="12" fill="#C7D2FE"/>
      <circle cx="165" cy="82" r="6" fill="#FCD9BD"/>
      <path d="M153 115 L153 100 Q165 93 177 100 L177 115" fill="#C7D2FE"/>
      <rect x="60" y="105" width="80" height="35" rx="4" fill="#F3F4F6"/>
      <text x="72" y="128" fill="#6366F1" font-size="16" font-weight="bold">+1.2K</text>
    </svg>`
  },
  {
    id: 'illus-likes',
    type: 'illustration',
    name: 'Likes',
    tags: ['likes', 'herzen', 'beliebt', 'engagement', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M100 130 C50 90 20 60 20 40 C20 15 45 5 65 5 C80 5 93 15 100 30 C107 15 120 5 135 5 C155 5 180 15 180 40 C180 60 150 90 100 130 Z" fill="#EF4444"/>
      <path d="M100 115 C60 85 40 60 40 45 C40 25 58 18 72 18 C83 18 93 25 100 38 C107 25 117 18 128 18 C142 18 160 25 160 45 C160 60 140 85 100 115 Z" fill="#F87171"/>
      <circle cx="50" cy="55" r="8" fill="white" opacity="0.3"/>
      <text x="155" y="130" fill="#6366F1" font-size="18" font-weight="bold">256</text>
    </svg>`
  },
  {
    id: 'illus-content',
    type: 'illustration',
    name: 'Content erstellen',
    tags: ['content', 'erstellen', 'post', 'kreativ', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="40" y="20" width="120" height="90" rx="8" fill="white" stroke="#E5E7EB" stroke-width="2"/>
      <rect x="50" y="30" width="60" height="45" rx="4" fill="#6366F1"/>
      <circle cx="70" cy="45" r="8" fill="#FDE68A"/>
      <polygon points="55,65 70,50 85,65 100,55 100,70 55,70" fill="#34D399"/>
      <line x1="50" y1="85" x2="100" y2="85" stroke="#E5E7EB" stroke-width="3"/>
      <line x1="50" y1="95" x2="80" y2="95" stroke="#E5E7EB" stroke-width="3"/>
      <rect x="115" y="30" width="35" height="25" rx="4" fill="#F3F4F6"/>
      <rect x="115" y="60" width="35" height="25" rx="4" fill="#F3F4F6"/>
      <circle cx="132" cy="42" r="6" fill="#6366F1"/>
      <rect x="120" y="70" width="25" height="8" rx="2" fill="#A5B4FC"/>
      <circle cx="155" cy="115" r="20" fill="#6366F1"/>
      <line x1="155" y1="105" x2="155" y2="125" stroke="white" stroke-width="4" stroke-linecap="round"/>
      <line x1="145" y1="115" x2="165" y2="115" stroke="white" stroke-width="4" stroke-linecap="round"/>
    </svg>`
  },
  {
    id: 'illus-viral',
    type: 'illustration',
    name: 'Viral gehen',
    tags: ['viral', 'trending', 'reichweite', 'share', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="75" r="20" fill="#6366F1"/>
      <circle cx="100" cy="75" r="35" fill="none" stroke="#6366F1" stroke-width="2" stroke-dasharray="5"/>
      <circle cx="100" cy="75" r="50" fill="none" stroke="#A5B4FC" stroke-width="2" stroke-dasharray="5"/>
      <circle cx="100" cy="75" r="65" fill="none" stroke="#C7D2FE" stroke-width="2" stroke-dasharray="5"/>
      <polygon points="95,70 95,80 105,75" fill="white"/>
      <circle cx="60" cy="45" r="10" fill="#6366F1"/>
      <circle cx="140" cy="45" r="10" fill="#A5B4FC"/>
      <circle cx="45" cy="90" r="8" fill="#A5B4FC"/>
      <circle cx="155" cy="90" r="8" fill="#6366F1"/>
      <circle cx="60" cy="120" r="10" fill="#C7D2FE"/>
      <circle cx="140" cy="120" r="10" fill="#C7D2FE"/>
      <line x1="100" y1="55" x2="75" y2="48" stroke="#6366F1" stroke-width="2"/>
      <line x1="100" y1="55" x2="125" y2="48" stroke="#A5B4FC" stroke-width="2"/>
      <line x1="80" y1="80" x2="55" y2="88" stroke="#A5B4FC" stroke-width="2"/>
      <line x1="120" y1="80" x2="145" y2="88" stroke="#6366F1" stroke-width="2"/>
    </svg>`
  },
  {
    id: 'illus-schedule',
    type: 'illustration',
    name: 'Beitrag planen',
    tags: ['planen', 'schedule', 'kalender', 'zeit', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="35" y="25" width="130" height="105" rx="8" fill="white" stroke="#E5E7EB" stroke-width="2"/>
      <rect x="35" y="25" width="130" height="25" rx="8" fill="#6366F1"/>
      <rect x="35" y="40" width="130" height="10" fill="#6366F1"/>
      <circle cx="55" cy="38" r="5" fill="white"/>
      <circle cx="145" cy="38" r="5" fill="white"/>
      <line x1="55" y1="15" x2="55" y2="30" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <line x1="145" y1="15" x2="145" y2="30" stroke="#6366F1" stroke-width="4" stroke-linecap="round"/>
      <g fill="#E5E7EB">
        <rect x="50" y="60" width="20" height="15" rx="2"/>
        <rect x="80" y="60" width="20" height="15" rx="2"/>
        <rect x="110" y="60" width="20" height="15" rx="2"/>
        <rect x="140" y="60" width="20" height="15" rx="2"/>
        <rect x="50" y="80" width="20" height="15" rx="2"/>
        <rect x="80" y="80" width="20" height="15" rx="2"/>
        <rect x="140" y="80" width="20" height="15" rx="2"/>
        <rect x="50" y="100" width="20" height="15" rx="2"/>
        <rect x="80" y="100" width="20" height="15" rx="2"/>
        <rect x="110" y="100" width="20" height="15" rx="2"/>
        <rect x="140" y="100" width="20" height="15" rx="2"/>
      </g>
      <rect x="110" y="80" width="20" height="15" rx="2" fill="#6366F1"/>
      <polyline points="115,87 118,90 125,83" stroke="white" stroke-width="2" fill="none"/>
    </svg>`
  },
  {
    id: 'illus-analytics-social',
    type: 'illustration',
    name: 'Social Analytics',
    tags: ['analytics', 'statistik', 'wachstum', 'daten', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="20" width="140" height="100" rx="8" fill="white" stroke="#E5E7EB" stroke-width="2"/>
      <line x1="50" y1="100" x2="50" y2="40" stroke="#E5E7EB" stroke-width="2"/>
      <line x1="50" y1="100" x2="150" y2="100" stroke="#E5E7EB" stroke-width="2"/>
      <path d="M60 90 L80 75 L100 80 L120 50 L140 40" stroke="#6366F1" stroke-width="3" fill="none" stroke-linecap="round"/>
      <circle cx="60" cy="90" r="4" fill="#6366F1"/>
      <circle cx="80" cy="75" r="4" fill="#6366F1"/>
      <circle cx="100" cy="80" r="4" fill="#6366F1"/>
      <circle cx="120" cy="50" r="4" fill="#6366F1"/>
      <circle cx="140" cy="40" r="4" fill="#6366F1"/>
      <rect x="45" y="125" width="30" height="15" rx="4" fill="#6366F1"/>
      <rect x="85" y="125" width="30" height="15" rx="4" fill="#A5B4FC"/>
      <rect x="125" y="125" width="30" height="15" rx="4" fill="#C7D2FE"/>
      <polygon points="135,35 145,30 145,40" fill="#10B981"/>
      <text x="148" y="38" fill="#10B981" font-size="10">+25%</text>
    </svg>`
  },
  {
    id: 'illus-hashtag',
    type: 'illustration',
    name: 'Hashtag',
    tags: ['hashtag', 'trend', 'tag', 'suche', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="70" y1="30" x2="55" y2="120" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <line x1="130" y1="30" x2="115" y2="120" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <line x1="40" y1="55" x2="150" y2="55" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <line x1="35" y1="95" x2="145" y2="95" stroke="#6366F1" stroke-width="12" stroke-linecap="round"/>
      <circle cx="165" cy="35" r="20" fill="#A5B4FC"/>
      <text x="155" y="42" fill="white" font-size="18" font-weight="bold">1</text>
    </svg>`
  },
  {
    id: 'illus-story',
    type: 'illustration',
    name: 'Story',
    tags: ['story', 'stories', 'ephemeral', 'content', 'socialmedia'],
    category: 'social',
    primaryColor: '#6366F1',
    illustrationStyle: 'flat',
    svg: `<svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="60" y="10" width="80" height="130" rx="8" fill="#1F2937"/>
      <rect x="65" y="15" width="70" height="120" rx="4" fill="#6366F1"/>
      <circle cx="100" cy="35" r="15" fill="white" stroke="#A5B4FC" stroke-width="3"/>
      <circle cx="100" cy="35" r="8" fill="#FCD9BD"/>
      <rect x="75" y="60" width="50" height="8" rx="2" fill="white" opacity="0.8"/>
      <rect x="80" y="72" width="40" height="6" rx="2" fill="white" opacity="0.6"/>
      <rect x="75" y="100" width="50" height="20" rx="4" fill="white"/>
      <text x="82" y="114" fill="#6366F1" font-size="10">Mehr sehen</text>
      <circle cx="30" cy="75" r="20" fill="#E5E7EB" stroke="#A5B4FC" stroke-width="3" stroke-dasharray="3"/>
      <circle cx="170" cy="75" r="20" fill="#E5E7EB" stroke="#A5B4FC" stroke-width="3" stroke-dasharray="3"/>
      <text x="23" y="80" fill="#9CA3AF" font-size="16">+</text>
      <text x="163" y="80" fill="#9CA3AF" font-size="16">+</text>
    </svg>`
  },
];

// ===== EXPORT ALLE KATEGORIEN =====
export interface IllustrationCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  illustrations: IllustrationElement[];
}

export const ILLUSTRATION_CATEGORIES: IllustrationCategory[] = [
  {
    id: 'business',
    name: 'Business',
    icon: '💼',
    description: 'Meeting, Praesentation, Team, Analytics',
    illustrations: businessIllustrations,
  },
  {
    id: 'technology',
    name: 'Technologie',
    icon: '💻',
    description: 'Coding, Geraete, Cloud, KI',
    illustrations: technologyIllustrations,
  },
  {
    id: 'people',
    name: 'Menschen',
    icon: '👥',
    description: 'Arbeiten, Entspannen, Feiern',
    illustrations: peopleIllustrations,
  },
  {
    id: 'nature',
    name: 'Natur',
    icon: '🌿',
    description: 'Pflanzen, Landschaften, Wetter',
    illustrations: natureIllustrations,
  },
  {
    id: 'abstract',
    name: 'Abstrakt',
    icon: '🎨',
    description: 'Formen, Muster, Dekorativ',
    illustrations: abstractIllustrations,
  },
  {
    id: 'social',
    name: 'Social Media',
    icon: '📱',
    description: 'Follower, Likes, Content',
    illustrations: socialMediaIllustrations,
  },
];

// ===== HELPER FUNCTIONS =====

/**
 * Gibt alle Illustrationen als flaches Array zurueck
 */
export function getAllIllustrations(): IllustrationElement[] {
  return ILLUSTRATION_CATEGORIES.flatMap(cat => cat.illustrations);
}

/**
 * Sucht Illustrationen nach Name oder Tags
 */
export function searchIllustrations(query: string): IllustrationElement[] {
  const q = query.toLowerCase();
  return getAllIllustrations().filter(illus =>
    illus.name.toLowerCase().includes(q) ||
    illus.tags.some(tag => tag.toLowerCase().includes(q)) ||
    (illus.category && illus.category.toLowerCase().includes(q))
  );
}

/**
 * Findet eine Illustration anhand ihrer ID
 */
export function getIllustrationById(id: string): IllustrationElement | undefined {
  return getAllIllustrations().find(illus => illus.id === id);
}

/**
 * Filtert Illustrationen nach Stil
 */
export function getIllustrationsByStyle(style: 'outline' | 'flat' | 'duotone'): IllustrationElement[] {
  return getAllIllustrations().filter(illus => illus.illustrationStyle === style);
}

/**
 * Gibt die Anzahl aller Illustrationen zurueck
 */
export function getIllustrationCount(): number {
  return getAllIllustrations().length;
}
